<template>
  <!-- The entity-form L width (860): the new-client form is pushed into this sheet, and a step
       is as wide as its sheet. The form guards the sheet while it is open (useSheetLayerGuard). -->
  <CodSheet
    ref="sheetRef"
    v-model="isOpen"
    width="860px"
    height="90dvh"
    :root="clientListComponent"
    :root-props="rootParams"
  />
</template>

<script setup lang="ts">
import { PropType, computed, markRaw, ref, watch } from 'vue';
import { ISelectClientConfig } from '@/types/components';
import { Client } from 'app-core';
import { useClient } from '@/composables/useClient';
import { CodSheet } from 'app-components';
import ClientList from '@/components/ClientList.vue';
import { ClientForm } from 'app-components';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: false
  },
  config: {
    type: Object as PropType<ISelectClientConfig | undefined>,
    default: () => undefined
  }
})

const emit = defineEmits(['update:modelValue'])
const { updatedClient } = useClient()

const sheetRef = ref<InstanceType<typeof CodSheet> | null>(null)

const isOpen = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v)
})

const clientListComponent = ref(markRaw(ClientList))
const rootParams = computed(() => {
  return {
    multiple: props.config?.multiple || false,
    selected: props.config?.selected || [],
    selectable: true,
    onSelect: onSelect,
    onCancel: close,
    openItemForm: openItemForm
  }
})

const cancel = ref(false)

// Any close not resolved by a selection (backdrop, ESC, or the host flipping
// the model) cancels exactly once. immediate: lazily mounted with the model
// already open (see MainLayout).
watch(() => props.modelValue, (val) => {
  if(val) {
    onModalPresent()
    return
  }

  if(cancel.value) {
    cancel.value = false
    props.config?.cancelHandler?.()
  }
}, { immediate: true })

function onModalPresent() {
  cancel.value = true
  updatedClient.value = undefined
}

function close() {
  emit('update:modelValue', false)
}

function onSelect(selected: Client[]) {
  props.config?.confirmHandler?.(selected)
  cancel.value = false

  close()
}

function onDetailsSave(client: Client) {
  updatedClient.value = client
  goBack()
}

function goBack() {
  sheetRef.value?.goBack()
}

function openItemForm(client?: Client) {
  updatedClient.value = undefined

  sheetRef.value?.navigate(ClientForm, {
    client: client,
    onSave: onDetailsSave,
    onCancel: goBack
  })
}
</script>
